import React from 'react';
import { Grid, Tooltip } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { Icon, ImageAvatar, SquareChip, TagsArray, Text, Utils } from 'app/main/components';
import { useSettings } from 'app/main/components/hooks';
import { ButtonLinkMenu } from 'app/main/components/buttons';
import buttons from './CompaniesButtons';

export default function CompaniesListItem(props: ItemLayoutProps) {
	const { t } = useSettings();

	const businessCardUrl = `${props.model.comp_uid}/${props.model.comp_business_card_front}`;

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="companies"
			uid={props.model.comp_uid}
			avatar={props.model.comp_avatar}
			initials={props.model.comp_title_initials}
			labelBadge={props.model.core_title_initials}
			labelBadgeTooltip={props.model.core_title}
			badgeColor={props.model.core_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 960 ? 6 : props.width >= 600 ? 8 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.comp_warning_desc && (
								<Tooltip title={props.model.comp_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.comp_blocked_desc && (
								<Tooltip title={props.model.comp_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* STATUS */}
							{props.model.comp_cost_id && props.model.comp_cost_id !== 1 && (
								<Tooltip title={props.model.cost_title} arrow>
									<Icon className="mr-2" icon="bell-sleep" color={props.model.cost_color} />
								</Tooltip>
							)}
							{/* LEAD */}
							{props.model.lead_comp_title && (
								<Tooltip title={props.model.lead_comp_title} arrow>
									<Icon className="mr-2" pack="ic" icon="round-person-pin" />
								</Tooltip>
							)}
							{/* BUSINESS CARD */}
							{props.model.comp_business_card_front && (
								<Tooltip
									title={
										<img
											className="h-full w-full object-cover"
											src={`${import.meta.env.VITE_ENDPOINT}/resources/companies/${businessCardUrl}`}
											loading="lazy"
											alt="business-card-front"
										/>
									}
									arrow
								>
									<Icon className="mr-2" icon="card-bulleted" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'companies', uid: props.model.comp_uid }}
							>
								{props.model.comp_erp_customer_id && `[${props.model.comp_erp_customer_id}] `}
								{props.model.comp_erp_supplier_id && `[${props.model.comp_erp_supplier_id}] `}
								{props.model.comp_title}
								{props.model.parent_comp_title && ` - ${props.model.parent_comp_title}`}
							</Text>
						</Grid>
						{/* ADDRESS */}
						<Grid size={12}>
							<Text className="font-400" variant="subtitle2" color="text.secondary">
								{props.model.comp_address || ''}
							</Text>
						</Grid>
						<Grid size={12}>
							<Text className="font-400" variant="subtitle2" color="text.secondary">
								{Utils.formatAddress2({
									t,
									address: {
										zip: props.model.comp_zip,
										city: props.model.comp_city,
										district: props.model.dist_iso || props.model.regi_iso,
										country: props.model.ctry_title
									}
								})}
							</Text>
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 600 && (
					<Grid size={props.width >= 960 ? 3 : 4}>
						<Grid container>
							<Grid size={12}>
								{/* MAILS */}
								<ButtonLinkMenu
									module="mails"
									list={props.model.comp_mails}
									table="companies"
									tableIcons={{ companies: 'email', people: 'card-account-mail' }}
									href={item => `mailto:${item.mail_title}`}
									fallback={t('VIEW_ITEM_NO_MAIL')}
								/>
							</Grid>
							<Grid size={12}>
								{/* PHONES */}
								<ButtonLinkMenu
									module="phones"
									list={props.model.comp_phones}
									table="companies"
									tableIcons={{ companies: 'phone', people: 'contact-phone' }}
									href={item => `tel:${item.phon_title}`}
									fallback={t('VIEW_ITEM_NO_PHONE')}
								/>
							</Grid>
						</Grid>
					</Grid>
				)}
				{props.width >= 960 && (
					<Grid size={3}>
						<Grid container>
							<Grid size={12}>
								{/* TYPE */}
								{props.model.coty_title && (
									<SquareChip
										label={props.model.coty_title}
										backgroundColor={props.model.coty_color}
									/>
								)}
								{/* SUBTYPES */}
								{props.model.comp_companies_subtypes?.map(subtype => (
									<SquareChip
										key={subtype.cosa_id}
										label={subtype.cosy_title}
										backgroundColor={subtype.cosy_color}
									/>
								))}
							</Grid>
							<Grid size={12}>
								{/* TAGS */}
								<TagsArray tags={props.model.comp_tags} changeFilter={props.changeFilter} />
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
